/**
 * Input required to decide whether a wheel event should scroll the grid
 * horizontally (Shift + wheel), and compute the resulting scrollLeft.
 */
export interface ShiftWheelScrollInput {
  shiftKey: boolean
  deltaX: number
  deltaY: number
  /** WheelEvent.deltaMode — 0: pixels, 1: lines, 2: pages */
  deltaMode: number
  rowHeight: number
  /** Viewport height in pixels — used to convert DOM_DELTA_PAGE deltas. */
  gridHeight: number
  scrollLeft: number
  scrollWidth: number
  clientWidth: number
}

/**
 * Chromium and Firefox on Windows/Linux translate Shift + mouse wheel into
 * horizontal scroll in their default wheel action. When the spreadsheet's
 * custom wheel handler calls preventDefault() (ratio mode for very large
 * tables), that default conversion is suppressed — this helper lets the
 * handler honour Shift + wheel by mapping `deltaY` onto the scroll container's
 * `scrollLeft`.
 *
 * Returns the target scrollLeft, or `null` when the event isn't a
 * Shift-triggered horizontal scroll (the caller should fall through to its
 * normal vertical-scroll handling).
 *
 * - When `deltaX !== 0` the browser has already populated the horizontal axis
 *   (e.g. native trackpad swipe), so this returns null to let the existing
 *   deltaX branch handle it without double-scrolling.
 * - `deltaMode` is converted to pixels the same way vertical deltas are
 *   normalised elsewhere in the handler.
 */
export function computeShiftWheelScrollLeft(input: ShiftWheelScrollInput): number | null {
  if (!input.shiftKey || input.deltaY === 0 || input.deltaX !== 0) return null

  let dx = input.deltaY
  if (input.deltaMode === 1) dx *= input.rowHeight
  else if (input.deltaMode === 2) dx *= input.gridHeight

  const maxScrollLeft = Math.max(0, input.scrollWidth - input.clientWidth)
  return Math.max(0, Math.min(maxScrollLeft, input.scrollLeft + dx))
}
